import { z } from 'zod'

// Communications, Phase 4 (ADR 0037; docs/architecture/BRE-Communications-Plan.md
// §5.3, §6, §11): myBRE push. Shared by Platform Core (which decides, renders
// and caps), the web Worker (which asks myBRE to deliver, through the
// loyalty.push.send command in source-commands.ts) and the staff UI (which
// shows the same rendering as the preview). Nothing here sends anything.
//
// A push goes to a myBRE account (a comms_address of kind 'mybre', the BRE
// Auth subject), never to a device: myBRE owns the subscriptions and reports
// how each device went. Hub keeps no endpoint and no key.

// ------------------------------------------------------------- templates

// What a notification shows: a title, a line of text, and the page it opens
// (myBRE's home when empty). Limits are what lock screens show without
// cutting off (iOS and Android both truncate a title past about 50
// characters and a body past about 2 lines; unverified per device).
export const PUSH_TITLE_LIMIT = 60
export const PUSH_BODY_LIMIT = 180
export const PUSH_URL_LIMIT = 500

export const PushTemplateContentSchema = z.object({
  title: z.string().trim().min(1).max(PUSH_TITLE_LIMIT),
  body: z.string().trim().min(1).max(PUSH_BODY_LIMIT),
  // An https link, or {{mybre_url}}, or null for myBRE's home.
  url: z.string().trim().max(PUSH_URL_LIMIT).nullable(),
})
export type PushTemplateContent = z.infer<typeof PushTemplateContentSchema>

export const PUSH_MERGE_FIELDS = {
  first_name: 'First name',
  event_name: 'Event name',
  event_date: 'Event date',
  mybre_url: 'myBRE link',
} as const
export type PushMergeField = keyof typeof PUSH_MERGE_FIELDS
export const PUSH_MERGE_FIELD_KEYS = Object.keys(PUSH_MERGE_FIELDS) as PushMergeField[]
export type PushMergeValues = { first_name: string | null; event_name: string | null; event_date: string | null; mybre_url: string | null }

const FIELD = /\{\{\s*([a-z_]+)\s*(?:\|([^}]*))?\}\}/g
const CONTROL = /[\u0000-\u001f\u007f]/

// What's wrong with a push template, in words staff can act on (empty = fine).
export function pushTemplateIssues(content: PushTemplateContent): string[] {
  const issues: string[] = []
  for (const [where, text] of [
    ['title', content.title],
    ['text', content.body],
    ['link', content.url ?? ''],
  ] as const) {
    if (CONTROL.test(text)) issues.push(`The ${where} has a control character`)
    for (const m of text.matchAll(FIELD)) if (!(PUSH_MERGE_FIELD_KEYS as string[]).includes(m[1]!)) issues.push(`{{${m[1]}}} isn’t a push merge field (use ${PUSH_MERGE_FIELD_KEYS.map((k) => `{{${k}}}`).join(', ')})`)
  }
  const url = content.url?.trim() ?? ''
  if (url && url !== '{{mybre_url}}' && !/^https:\/\/[^\s]+$/.test(url)) issues.push('The link must start with https:// (or be {{mybre_url}})')
  if (/https?:\/\//i.test(content.title) || /https?:\/\//i.test(content.body)) issues.push('Put the link in the link field, not in the text: a notification opens one page')
  return [...new Set(issues)]
}

export class PushRenderError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'PushRenderError'
  }
}

const fill = (text: string, merge: PushMergeValues) =>
  text.replace(FIELD, (_, key: string, fallback: string | undefined) => {
    const value = (merge as Record<string, string | null>)[key]
    return value && value.trim() ? value.trim() : (fallback ?? '').trim()
  })

const clip = (text: string, max: number) => (text.length <= max ? text : `${text.slice(0, max - 1).trimEnd()}…`)

export type RenderedPush = { title: string; body: string; url: string | null; test: boolean }

// The notification as the device shows it. Merge values are plain text (a
// notification renders no markup); an overlong result is cut with an
// ellipsis rather than refused, because a first name can't be known when
// the template is written. A test is marked in its title.
export function renderPush(input: { content: PushTemplateContent; merge: PushMergeValues; test?: boolean }): RenderedPush {
  const issues = pushTemplateIssues(input.content)
  if (issues.length) throw new PushRenderError(issues.join('; '))
  const title = fill(input.content.title, input.merge).replace(/\s+/g, ' ').trim()
  const body = fill(input.content.body, input.merge).replace(/\s+/g, ' ').trim()
  if (!title || !body) throw new PushRenderError('The notification would have an empty title or text')
  const rawUrl = input.content.url?.trim() ? fill(input.content.url.trim(), input.merge) : (input.merge.mybre_url ?? null)
  if (rawUrl && !/^https:\/\/\S+$/.test(rawUrl)) throw new PushRenderError('The link must be https')
  return {
    title: clip(input.test ? `[TEST] ${title}` : title, PUSH_TITLE_LIMIT),
    body: clip(body, PUSH_BODY_LIMIT),
    url: rawUrl ? rawUrl.slice(0, PUSH_URL_LIMIT) : null,
    test: input.test === true,
  }
}

// ------------------------------------------------------------- settings

export type PushSettings = {
  pushProvider: 'mybre' | 'none'
  pushCapCount: number
  pushCapHours: number
  personCapCount: number
  personCapHours: number
  eventPush: boolean
  eventSms: boolean
  eventEmail: boolean
  btowntixSendsEventEmail: boolean
  eventAlertMode: 'confirm'
  eventBrandKey: string
  holdersLookaheadDays: number
  updatedAt: string | null
  updatedByName: string | null
}

export const PushSettingsSchema = z
  .object({
    pushProvider: z.enum(['mybre', 'none']),
    pushCapCount: z.number().int().min(1).max(50),
    pushCapHours: z.number().int().min(1).max(744),
    personCapCount: z.number().int().min(1).max(50),
    personCapHours: z.number().int().min(1).max(744),
    eventPush: z.boolean(),
    eventSms: z.boolean(),
    eventEmail: z.boolean(),
    btowntixSendsEventEmail: z.boolean(),
    eventBrandKey: z.string().min(2).max(40),
    holdersLookaheadDays: z.number().int().min(1).max(62),
  })
  .refine((s) => !(s.eventEmail && s.btowntixSendsEventEmail), { message: 'BTownTix already emails ticket holders about their event', path: ['eventEmail'] })

// The duplicate rule (ADR 0037): the channels Hub may use for a message to
// ticket holders about their event. Email only when BTownTix has stopped
// sending its own.
export function eventChannels(s: Pick<PushSettings, 'eventPush' | 'eventSms' | 'eventEmail' | 'btowntixSendsEventEmail'>): ('push' | 'sms' | 'email')[] {
  const out: ('push' | 'sms' | 'email')[] = []
  if (s.eventPush) out.push('push')
  if (s.eventSms) out.push('sms')
  if (s.eventEmail && !s.btowntixSendsEventEmail) out.push('email')
  return out
}

// ------------------------------------------------------------- provider

export type PushProviderStatus = {
  provider: 'mybre' | 'none'
  mode: 'live' | 'simulated' | 'not_set_up'
  reason: string
}

// One rendered push, claimed, for the web Worker to hand to myBRE.
export type PushEnvelope = {
  messageId: string
  claimId: string
  sendClass: 'marketing' | 'operational'
  purpose: 'marketing' | 'service' | 'operational'
  // The BRE Auth subject.
  subject: string
  title: string
  body: string
  url: string | null
  tag: string | null
  brandKey: string
  campaignId: string | null
  test: boolean
}

// What myBRE's devices did, summarized for a message's evidence.
export function pushOutcomeDetail(r: { delivery: string; devices: number; sent: number; simulated: number; gone: number; failed: number }): string {
  if (r.devices === 0) return 'No device has myBRE push turned on'
  const parts = [r.sent ? `sent to ${r.sent}` : null, r.simulated ? `simulated on ${r.simulated}` : null, r.gone ? `${r.gone} gone (removed by myBRE)` : null, r.failed ? `${r.failed} failed` : null].filter(Boolean)
  return `${r.devices} device${r.devices === 1 ? '' : 's'}: ${parts.join(', ')}`
}
